import { useState, useRef, useEffect, useCallback } from "react";
import { Bot, X, Send, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem("bb_chat_session");
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("bb_chat_session", id);
  }
  return id;
};

export const AIChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      fetch(`${API}/api/ai-chat/history/${getSessionId()}`)
        .then((r) => r.json())
        .then((d) => {
          if (d.messages?.length) setMessages(d.messages.map((m) => ({ role: m.role, content: m.content })));
        })
        .catch(() => {});
    }
  }, [open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/api/ai-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          } else if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload.error };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection error. Please try again or call +91-9464645491." };
        return copy;
      });
    }
    setStreaming(false);
  }, [input, streaming]);

  return (
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-chat-toggle"
        aria-label="Ask BrandBorn AI"
        className="group fixed bottom-[5.75rem] right-5 z-50 flex items-center justify-center w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-700 text-white rounded-full shadow-[0_4px_20px_rgba(220,38,38,0.45)] hover:shadow-[0_10px_32px_rgba(220,38,38,0.55)] transition-all duration-300 hover:scale-110"
      >
        <Sparkles size={24} className="text-white" />
        <span className="absolute right-16 px-3 py-1.5 bg-gray-900 text-white text-xs font-bold rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-xl">
          Ask BrandBorn AI
        </span>
        <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-blue-400 border-2 border-white rounded-full animate-pulse" />
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-40 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm bg-white rounded-3xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden" style={{ height: "min(520px, 70vh)" }} data-testid="ai-chat-panel">
          {/* Header */}
          <div className="bg-gray-950 text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center"><Bot size={18} /></div>
              <div>
                <p className="font-black text-sm">BrandBorn AI</p>
                <p className="text-[10px] text-gray-400 font-semibold">Powered by Gemini • Instant answers</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-chat-close" aria-label="Close chat" className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"><X size={18} /></button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <Sparkles className="mx-auto text-blue-500 mb-2" size={22} />
                <p className="text-sm font-bold text-gray-800 mb-1">Hi! I'm BrandBorn AI 👋</p>
                <p className="text-xs text-gray-500 mb-4">Ask me anything about marketplace onboarding, Myntra, Blinkit, documents, timelines...</p>
                <div className="space-y-2">
                  {["How do I sell on Myntra?", "Documents needed for Blinkit?", "What is EcomCatalog.ai?"].map((s, i) => (
                    <button key={i} onClick={() => setInput(s)} data-testid={`ai-chat-suggestion-${i}`} className="block w-full text-left px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 hover:border-blue-300 hover:bg-blue-50 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-red-600 text-white rounded-br-md" : "bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm"}`} data-testid={`ai-chat-msg-${i}`}>
                  {m.content || (streaming && i === messages.length - 1 ? <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:120ms]" /><span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:240ms]" /></span> : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-100 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your question..."
              data-testid="ai-chat-input"
              className="flex-1 px-4 py-2.5 bg-slate-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-400 text-gray-800"
            />
            <button onClick={send} disabled={streaming || !input.trim()} data-testid="ai-chat-send" aria-label="Send" className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 transition-colors">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
